import { DateFields } from '@/src/types/itinerary'
import dayjs from 'dayjs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { DateSelectorInput } from './dateSelectorInput'

interface DateSelectorInputWrapperProps {
    dateValue: DateFields
    days: number[]
    months: string[]
    years: number[]
    onChange: (date: DateFields) => void
    size?: 'small' | 'medium'
    disabled?: boolean
    onBlur?: () => void
}

const DateSelectorInputWrapper: FC<DateSelectorInputWrapperProps> = ({
    dateValue,
    days,
    months,
    years,
    onChange,
    size = 'small',
    disabled = false,
    onBlur
}) => {
    // Local state to manage the date internally
    const [localDate, setLocalDate] = useState<DateFields>(dateValue)

    // Update local state when props change
    useEffect(() => {
        setLocalDate(dateValue)
    }, [dateValue])

    // Get month index from month name
    const getMonthIndex = useCallback(
        (monthName: string): number => {
            return months.findIndex(m => m === monthName) + 1
        },
        [months]
    )

    // Get maximum days for a given month and year
    const getMaxDaysForMonth = useCallback(
        (monthName: string, year: string): number => {
            if (!monthName || !year) return 31

            const monthIndex = getMonthIndex(monthName)
            if (!monthIndex) return 31

            const yearNum = parseInt(year)
            if (!yearNum) return 31

            return dayjs(`${yearNum}-${monthIndex.toString().padStart(2, '0')}-01`).daysInMonth()
        },
        [getMonthIndex]
    )

    // Validate and adjust date when any field changes
    const handleDateChange = useCallback(
        (field: keyof DateFields, value: string) => {
            let newDate = { ...localDate, [field]: value }

            // If we have all three values, validate the date
            if (newDate.day && newDate.month && newDate.year) {
                const maxDays = getMaxDaysForMonth(newDate.month, newDate.year)
                const selectedDay = parseInt(newDate.day)

                // If selected day exceeds max days for the month, adjust it
                if (selectedDay > maxDays) {
                    newDate.day = maxDays.toString()
                }
            }

            // Update local state
            setLocalDate(newDate)

            // Call parent onChange
            onChange(newDate)
        },
        [localDate, onChange, getMaxDaysForMonth]
    )

    // Generate valid days for the selected month and year
    const validDays = useMemo(() => {
        if (!localDate.month || !localDate.year) {
            return days
        }

        const maxDays = getMaxDaysForMonth(localDate.month, localDate.year)
        return days.filter(day => day <= maxDays)
    }, [localDate.month, localDate.year, days, getMaxDaysForMonth])

    // Auto-adjust day when month or year changes
    useEffect(() => {
        if (localDate.day && localDate.month && localDate.year) {
            const maxDays = getMaxDaysForMonth(localDate.month, localDate.year)
            const selectedDay = parseInt(localDate.day)

            if (selectedDay > maxDays) {
                const adjustedDate = { ...localDate, day: maxDays.toString() }
                setLocalDate(adjustedDate)
                onChange(adjustedDate)
            }
        }
    }, [localDate.month, localDate.year, localDate.day, getMaxDaysForMonth, onChange])

    return (
        <DateSelectorInput
            dateValue={localDate}
            validDays={validDays}
            months={months}
            years={years}
            onDateChange={handleDateChange}
            size={size}
            disabled={disabled}
            onBlur={onBlur}
        />
    )
}

export { DateSelectorInputWrapper }
export default DateSelectorInputWrapper
